import {
  Component,
  OnInit,
  Inject,
  PLATFORM_ID,
  Renderer2,
  OnDestroy,
  InjectionToken,
  ChangeDetectorRef,
  inject,
  effect,
} from '@angular/core';

import { isPlatformBrowser, DOCUMENT, isPlatformServer } from '@angular/common';
import { Router, NavigationEnd, RouterOutlet, NavigationStart, NavigationCancel } from '@angular/router';
import { Store } from '@ngrx/store';
import { getDictionaryLoaded } from '@teamfoster/sdk/dictionary-ngrx';
import { Observable, BehaviorSubject, of, Subject, combineLatest } from 'rxjs';

import * as navActions from './store/actions/nav.action';
import * as navSelectors from './store/selectors/nav.selector';
import { tap, take, filter, withLatestFrom, takeUntil, map, switchMap, pairwise } from 'rxjs/operators';
import { routerFade } from './animations';
import { ChangeDetectionStrategy } from '@angular/core';
import { CookieConfig, CookieConfigService, CookiePreferences, getCookiePreferences } from '@teamfoster/sdk/cookie-ngrx';
import { ActionBar } from './action-bars/models';
import { getActiveActionBar } from './action-bars/store';
import { getMenuLoaded } from '@teamfoster/sdk/menu-ngrx';
import * as fromShop from './shop/store';
import { Cart, Customer } from './shop';
import { Line } from './shop/models/cart.model';
import { getActiveLanguage, getSwitchableLanguages } from '@teamfoster/sdk/language-ngrx';
import { environment } from 'src/environments/environment';
import { RouterReducerState } from '@ngrx/router-store';
import { RouterStateUrl, getRouterState } from './store';
import { getNewsLetterShowWindow } from './newsletters/store';
import { ChatStatus } from './core/models/chatStatus.model';
import { getAllSocialChannels } from './social-media/store';
import { Analyzify } from './utility/analyzify.utility';
import { MetaPixel } from './utility/meta-pixel.utility';
import { getActiveStorefront } from './storefronts';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  animations: [routerFade],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  private store = inject(Store);
  private document = inject(DOCUMENT);
  private platformId = inject(PLATFORM_ID);
  private router = inject(Router);
  private renderer = inject(Renderer2);
  private cookieConfig = inject(CookieConfigService);
  private cd = inject(ChangeDetectorRef);

  loading$ = new BehaviorSubject<boolean>(true);
  loaded$!: Observable<boolean>;
  menuOpen$!: Observable<boolean>;
  searchOpen$!: Observable<boolean>;
  filtersOpen$!: Observable<boolean>;
  menuTransparent$!: Observable<boolean>;
  cartPaneOpen$!: Observable<boolean>;
  theme$!: Observable<string>;
  actionBar$!: Observable<ActionBar>;
  cookiePrefs$!: Observable<CookiePreferences | null>;
  cart$?: Observable<Cart | undefined>;
  chatStatus$?: Observable<ChatStatus | undefined>;
  activeStorefront$ = this.store.select(getActiveStorefront);
  socialChannels$ = this.store.selectSignal(getAllSocialChannels);

  showNewsletter$ = this.store.select(getNewsLetterShowWindow);
  customer$ = this.store.selectSignal(fromShop.getCustomer);
  customerHasVatNumber$ = this.store.selectSignal(fromShop.getCustomerHasVatNumber);

  exemptionsChecking$ = this.store.selectSignal(fromShop.getExemptionChecking);
  exemptionsChecked$ = this.store.selectSignal(fromShop.getExemptionChecked);
  exemptionsValid$ = this.store.selectSignal(fromShop.getExemptionValid);

  subscription: any;

  languages$!: Observable<any[]>;
  activeLanguage$!: Observable<string | undefined>;
  domainName: string = '';

  routeState$?: Observable<RouterReducerState<RouterStateUrl>>;

  testGridEnabled = false;
  routerAnimationState = '';

  private _unsubscribe$ = new Subject<void>();

  customerEffect = effect(async () => {
    var customer = this.customer$();

    if (customer) {
      await this.identifyCustomer(customer as Customer);
    }
  });

  ngOnInit(): void {
    this.cookiePrefs$ = this.store.select(getCookiePreferences);

    this.cookiePrefs$
      .pipe(
        filter(a => a != null),
        take(1)
      )
      .subscribe(a => {
        if (a?.marketing) {
          this.loadKlaviyo();
          this.loadFacebookPixel(environment.pixelCode);
        }
      });

    this.routeState$ = this.store.select(getRouterState);

    // Language
    this.languages$ = this.store.select(getSwitchableLanguages);
    this.activeLanguage$ = this.store.select(getActiveLanguage);
    this.activeLanguage$.subscribe(a => {
      const langAttribute = a?.split('-')[0];
      this.document.documentElement.setAttribute('lang', langAttribute || 'nl');
    });

    // Overlay handlers
    this.menuOpen$ = this.store.select(navSelectors.getMenuOpen).pipe(tap(a => this.updateRoot(a)));
    this.searchOpen$ = this.store.select(navSelectors.getSearchOpen).pipe(tap(a => this.updateRoot(a)));
    this.store.select(navSelectors.getFiltersOpen).subscribe(a => {
      this.updateRoot(a);
      this.setChatStatus(a ? 'hidden' : 'default');
    });
    this.cartPaneOpen$ = this.store.select(navSelectors.getCartPaneOpen).pipe(tap(a => this.updateRoot(a, 'cart-pane--open')));

    this.menuTransparent$ = this.store.select(navSelectors.getNavTransparency);
    this.theme$ = this.store.select(navSelectors.getTheme);
    this.actionBar$ = this.store.select(getActiveActionBar);
    this.cart$ = this.store.select(fromShop.getCart);

    this.loaded$ = combineLatest([this.store.select(getDictionaryLoaded), this.store.select(getMenuLoaded)]).pipe(
      map(([dictionaryLoaded, menuLoaded]) => dictionaryLoaded && menuLoaded)
    );

    this.chatStatus$ = this.store.select(navSelectors.getChatStatus);
    this.chatStatus$.subscribe(a => {
      if (a) {
        this.setChatStatus(a);
      }
    });

    if (isPlatformBrowser(this.platformId)) {
      this.document.querySelector('body')?.classList.add('set--in');
      this.document.documentElement.style.setProperty('--vh', `${(<any>window).innerHeight * 0.01}px`);

      this.loadChat();
      this.router.events
        .pipe(
          // filter(e => e instanceof NavigationEnd),
          withLatestFrom(this.cookiePrefs$, this.routeState$),
          takeUntil(this._unsubscribe$)
        )
        .subscribe(([event, cookies, state]) => {
          if (event instanceof NavigationStart) {
            this.loading$.next(true);
            this.closeSidebar();
            if (isPlatformBrowser(this.platformId)) {
              if ((<any>window).dataLayer) {
                this.clearDataLayer();
              }
            }
          } else if (event instanceof NavigationEnd || event instanceof NavigationCancel) {
            this.loading$.next(false);
          }

          if (isPlatformBrowser(this.platformId) && event instanceof NavigationEnd) {
            if ((<any>window).gtag && cookies?.analytical) {
              (<any>window).gtag('config', this.cookieConfig.analyticsCode, {
                page_path: event.urlAfterRedirects,
                anonymize_ip: true,
              });
            }

            if (!state?.state?.queryParams || !Object.keys(state?.state?.queryParams).length) {
              (<any>window).scrollTo(0, 0);
            }
          }
        });

      // Analyzify Page View
      this.router.events
        .pipe(
          filter(e => e instanceof NavigationEnd),
          withLatestFrom(this.cookiePrefs$, this.routeState$),
          takeUntil(this._unsubscribe$),
          switchMap(([, cookies, state]) => {
            if (isPlatformBrowser(this.platformId)) {
              if ((<any>window).dataLayer && cookies?.analytical) {
                MetaPixel.PageView();
                return Analyzify.PageViewAsync(state?.state.url ? '' : 'homepage', this.customer$() as Customer);
              }
            }
            return of(null);
          })
        )
        .subscribe();
    }

    this.cookiePrefs$
      .pipe(
        takeUntil(this._unsubscribe$),
        pairwise(),
        filter(([a, b]) => a?.analytical !== b?.analytical && b!.analytical),
        map(([, b]) => b),
        withLatestFrom(this.routeState$)
      )
      .subscribe(([prefs, router]) => {
        if ((<any>window).gtag) {
          (<any>window).gtag('config', this.cookieConfig.analyticsCode, {
            page_path: router.state.url,
            anonymize_ip: true,
          });
        }
        if ((<any>window).dataLayer && prefs?.analytical) {
          MetaPixel.PageView();
          Analyzify.PageViewAsync(router.state.url ? '' : 'homepage', this.customer$() as Customer);
        }
      });
  }

  openNewsletterPopup() {
    if (!(window as any)['klaviyo']) {
      return;
    }
    (window as any)['klaviyo'].openForm('XvgKBU');
  }

  async identifyCustomer(customer: Customer) {
    if (isPlatformServer(this.platformId)) {
      return;
    }
    if (!(window as any)['klaviyo']) {
      return;
    }
    await (window as any)['klaviyo'].identify({
      email: customer.email,
    });
  }

  loadFacebookPixel(facebookPixelCode: string) {
    if (facebookPixelCode && typeof (<any>window).fbq === 'undefined') {
      const $script = document.createElement('script');
      $script.id = 'Facebook';
      $script.async = true;
      $script.innerHTML = `!function (f, b, e, v, n, t, s) {
            if (f.fbq) return; n = f.fbq = function () {
                n.callMethod ?
                    n.callMethod.apply(n, arguments) : n.queue.push(arguments)
            }; if (!f._fbq) f._fbq = n;
            n.push = n; n.loaded = !0; n.version = '2.0'; n.queue = []; t = b.createElement(e); t.async = !0;
            t.src = v; s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s)
        }(window,
            document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

        fbq.disablePushState = true; //not recommended, but can be done
        fbq('init', '${facebookPixelCode}');`;

      document.head.prepend($script);
    }
  }

  loadKlaviyo() {
    if ((<any>window).klaviyo === undefined) {
      const $script = document.createElement('script');
      $script.id = 'Klaviyo';
      $script.async = true;
      $script.type = 'text/javascript';
      $script.src = 'https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=TzwGaS';

      document.body.append($script);
    }
  }

  toggleMenu() {
    this.store.dispatch(navActions.ToggleMenu());
  }

  closeMenu() {
    this.store.dispatch(navActions.CloseMenu());
  }

  openMenu() {
    this.store.dispatch(navActions.OpenMenu());
  }

  closeSidebar() {
    this.store.dispatch(navActions.CloseCart());
  }

  closeOverlays() {
    this.closeMenu();
    this.store.dispatch(navActions.CloseSearch());
  }

  prepareRoute(outlet: RouterOutlet) {
    return outlet && outlet.activatedRouteData;
  }

  private updateRoot(overlayActive: boolean, cssClass: string = 'main-nav--active') {
    overlayActive ? this.renderer?.addClass(this.document.body, cssClass) : this.renderer?.removeClass(this.document.body, cssClass);
    this.cd.detectChanges();
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  private loadChat() {
    const baseScript = document.createElement('script');
    baseScript.async = true;
    baseScript.src =
      'https://cdn.shopify.com/shopifycloud/shopify_chat/storefront/shopifyChatV1.js?api_env=production&c=%23dea294&i=chat_bubble&p=bottom_left&s=text&shop_id=XwvZsEGKfQHxDr6RrCyH98JnXPzdLNe4dW4esOdmDAw&t=no_text&v=1&vp=lowest&shop=kado-design-v3.myshopify.com';
    document.body.append(baseScript);
  }

  toggleCart() {
    this.store.dispatch(navActions.ToggleCart());
  }

  setChatStatus(status: ChatStatus) {
    if (isPlatformBrowser(this.platformId)) {
      const el = document.querySelector('#dummy-chat-button-iframe');
      if (el) {
        el.className = ''; // reset all classes
        el.classList.add(status);
      }
    }
  }

  getNewLine(lines: Line[]) {
    if (!lines || !lines.length) {
      return null;
    }
    return [...lines]?.reverse().slice(0, 1)[0];
  }

  clearDataLayer() {
    if ((<any>window).dataLayer !== undefined && (<any>window).google_tag_manager !== undefined) {
      (<any>window).dataLayer.length = 0;
      const gtmContainerReg = /GTM-/i;

      for (const gtmKey of Object.keys((<any>window).google_tag_manager)) {
        if (
          gtmContainerReg.test(gtmKey) &&
          (<any>window).google_tag_manager[gtmKey].dataLayer &&
          (<any>window).google_tag_manager[gtmKey].dataLayer.reset
        ) {
          (<any>window).google_tag_manager[gtmKey].dataLayer.reset();
        }
      }
    }
  }
}
